/**
 * Tag manager — manager/admin only (PRD addendum Apr-2026).
 *
 * Curated taxonomy for contacts and accounts ("Architect",
 * "Distributor", etc). Tags are case-insensitively unique; the API
 * returns 409 with `{ code: "tag_name_exists", existing: Tag }` on
 * a clash, and the UI converts that into a "found existing tag" hint
 * rather than a hard error so the manager isn't stuck.
 *
 * Operations:
 *
 *   - Create: type a name, hit Enter / click +. Empty whitespace is
 *     trimmed; lengths are 1..40 (server-side validated; we mirror).
 *   - Rename: click pencil → inline input → save / cancel.
 *   - Delete: trash icon → confirm-when-in-use ("Tag X is on N
 *     contacts and M accounts; delete?"). Server cascades the join
 *     rows on FK delete.
 *   - Filter: a top-of-list `q` substring search calls the server.
 *
 * Server enforces role gating (Manager+) on every mutation. The
 * `beforeLoad` redirect just keeps reps from staring at a spinner
 * before the 403 lands.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import {
  AlertCircle,
  Check,
  Loader2,
  Pencil,
  Plus,
  Search,
  Tag as TagIcon,
  Trash2,
  X,
} from "lucide-react";
import {
  type FormEvent,
  type KeyboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { ApiError, type Tag, isTagNameClashDetail, tagsApi } from "../lib/api";
import { useAuthStore } from "../lib/auth";
import { toast } from "../lib/toast";

const MAX_NAME = 40;

export const Route = createFileRoute("/_app/settings/tags")({
  beforeLoad: () => {
    const user = useAuthStore.getState().user;
    if (user && user.role !== "admin" && user.role !== "manager") {
      throw redirect({ to: "/deals" });
    }
  },
  component: TagsAdminPage,
});

function TagsAdminPage() {
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 200);
    return () => clearTimeout(t);
  }, [query]);

  const queryClient = useQueryClient();

  const listQuery = useQuery({
    queryKey: ["tags", { q: debounced, with_counts: true }],
    queryFn: () => tagsApi.list({ ...(debounced ? { q: debounced } : {}), with_counts: true }),
  });

  const tags = listQuery.data?.items ?? [];

  const total = listQuery.data?.total ?? null;
  const usageStats = useMemo(() => {
    let inUse = 0;
    let unused = 0;
    for (const t of tags) {
      const n = (t.contact_count ?? 0) + (t.account_count ?? 0);
      if (n > 0) inUse++;
      else unused++;
    }
    return { inUse, unused };
  }, [tags]);

  const onChanged = () => {
    queryClient.invalidateQueries({ queryKey: ["tags"] });
    // Detail pages and list-page filter chips read tags too; refresh
    // them so a renamed tag updates everywhere on the next view.
    queryClient.invalidateQueries({ queryKey: ["contact-tags"] });
    queryClient.invalidateQueries({ queryKey: ["account-tags"] });
  };

  return (
    <div className="mx-auto max-w-3xl space-y-5 p-4 sm:p-6">
      <header className="space-y-1">
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
          <TagIcon className="size-5 opacity-70" aria-hidden />
          Tags
        </h1>
        <p className="text-sm opacity-60">
          Curated labels for contacts and accounts (e.g. "Architect", "Distributor"). Names are
          unique — the same tag cannot be created twice. Edits propagate everywhere the tag is in
          use.
        </p>
      </header>

      <CreateTagBar onCreated={onChanged} />

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="relative max-w-sm flex-1">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 opacity-50"
            aria-hidden
          />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter tags…"
            className="w-full rounded-md border border-[var(--border)] bg-transparent py-1.5 pl-8 pr-3 text-sm outline-none focus:border-[var(--accent)]"
          />
        </div>
        {total !== null && (
          <span className="text-xs opacity-60">
            {total} {total === 1 ? "tag" : "tags"}
            {tags.length > 0 && (
              <>
                {" "}
                · {usageStats.inUse} in use, {usageStats.unused} unused
              </>
            )}
          </span>
        )}
      </div>

      {listQuery.isLoading ? (
        <p className="text-sm opacity-60">Loading…</p>
      ) : listQuery.error ? (
        <p className="text-sm text-red-700 dark:text-red-300">
          Could not load tags: {(listQuery.error as Error).message}
        </p>
      ) : tags.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[var(--border)] p-10 text-center">
          <TagIcon className="mx-auto mb-3 size-8 opacity-40" aria-hidden />
          <p className="text-sm font-medium">{debounced ? "No tags match." : "No tags yet."}</p>
          <p className="mt-1 text-sm opacity-60">
            Create one above. Sales reps will see it on contacts and accounts.
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-[var(--border)] overflow-hidden rounded-xl border border-[var(--border)]">
          {tags.map((t) => (
            <TagRow key={t.id} tag={t} onChanged={onChanged} />
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Create row ─────────────────────────────────────────────────────

function CreateTagBar({ onCreated }: { onCreated: () => void }) {
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [existing, setExisting] = useState<Tag | null>(null);

  const createMutation = useMutation({
    mutationFn: (n: string) => tagsApi.create(n),
    onSuccess: () => {
      setName("");
      setError(null);
      setExisting(null);
      onCreated();
      toast.success("Tag created.");
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409 && isTagNameClashDetail(err.detail)) {
        setExisting(err.detail.existing);
        setError(null);
        return;
      }
      const msg = err instanceof ApiError ? err.message : "Could not create tag.";
      setError(msg);
      setExisting(null);
      toast.error(msg);
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const n = name.trim();
    if (!n) {
      setError("Tag name can't be empty.");
      return;
    }
    if (n.length > MAX_NAME) {
      setError(`Tag names are at most ${MAX_NAME} characters.`);
      return;
    }
    setError(null);
    createMutation.mutate(n);
  };

  return (
    <form onSubmit={onSubmit} className="space-y-2 rounded-xl border border-[var(--border)] p-4">
      <label htmlFor="new-tag-name" className="block text-xs font-medium opacity-80">
        New tag
      </label>
      <div className="flex items-center gap-2">
        <input
          id="new-tag-name"
          type="text"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setError(null);
            setExisting(null);
          }}
          maxLength={MAX_NAME}
          placeholder="e.g. Architect"
          className="flex-1 rounded-md border border-[var(--border)] bg-transparent px-3 py-2 text-sm outline-none focus:border-[var(--accent)]"
          autoComplete="off"
        />
        <button
          type="submit"
          disabled={createMutation.isPending || name.trim().length === 0}
          className="inline-flex items-center gap-1.5 rounded-md bg-[var(--accent)] px-3 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
        >
          {createMutation.isPending ? (
            <Loader2 className="size-4 animate-spin" aria-hidden />
          ) : (
            <Plus className="size-4" aria-hidden />
          )}
          Create
        </button>
      </div>
      {error && (
        <p className="flex items-center gap-1 text-xs text-red-700 dark:text-red-300">
          <AlertCircle className="size-3" aria-hidden /> {error}
        </p>
      )}
      {existing && (
        <p className="flex flex-wrap items-center gap-1 text-xs opacity-80">
          <AlertCircle className="size-3 opacity-70" aria-hidden />A tag named “
          <span className="font-medium">{existing.name}</span>” already exists — use that one (it's
          already in your list below).
        </p>
      )}
    </form>
  );
}

// ── Existing-tag row (rename + delete) ─────────────────────────────

function TagRow({ tag, onChanged }: { tag: Tag; onChanged: () => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(tag.name);
  const [error, setError] = useState<string | null>(null);
  const [existing, setExisting] = useState<Tag | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Focus the rename input when entering edit mode (replaces the
  // jsx-`autoFocus` pattern; biome flags that under a11y/noAutofocus).
  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const renameMutation = useMutation({
    mutationFn: (n: string) => tagsApi.rename(tag.id, n),
    onSuccess: () => {
      setEditing(false);
      setError(null);
      setExisting(null);
      onChanged();
      toast.success("Tag renamed.");
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409 && isTagNameClashDetail(err.detail)) {
        setExisting(err.detail.existing);
        setError(null);
        return;
      }
      const msg = err instanceof ApiError ? err.message : "Could not rename.";
      setError(msg);
      setExisting(null);
    },
  });

  const removeMutation = useMutation({
    mutationFn: () => tagsApi.remove(tag.id),
    onSuccess: () => {
      onChanged();
      toast.success("Tag deleted.");
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "Could not delete.";
      setError(msg);
      toast.error(msg);
    },
  });

  const startEdit = () => {
    setEditing(true);
    setDraft(tag.name);
    setError(null);
    setExisting(null);
  };

  const cancelEdit = () => {
    setEditing(false);
    setDraft(tag.name);
    setError(null);
    setExisting(null);
  };

  const submitRename = () => {
    const n = draft.trim();
    if (!n) {
      setError("Tag name can't be empty.");
      return;
    }
    if (n === tag.name) {
      cancelEdit();
      return;
    }
    if (n.length > MAX_NAME) {
      setError(`Tag names are at most ${MAX_NAME} characters.`);
      return;
    }
    renameMutation.mutate(n);
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      submitRename();
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancelEdit();
    }
  };

  const handleDelete = () => {
    const contactCount = tag.contact_count ?? 0;
    const accountCount = tag.account_count ?? 0;
    const inUseCount = contactCount + accountCount;
    let msg = `Delete tag “${tag.name}”?`;
    if (inUseCount > 0) {
      const parts: string[] = [];
      if (contactCount > 0)
        parts.push(`${contactCount} ${contactCount === 1 ? "contact" : "contacts"}`);
      if (accountCount > 0)
        parts.push(`${accountCount} ${accountCount === 1 ? "account" : "accounts"}`);
      msg = `Tag “${tag.name}” is in use on ${parts.join(" and ")}. Deleting it will remove the tag from those records. Delete anyway?`;
    }
    if (window.confirm(msg)) {
      setError(null);
      removeMutation.mutate();
    }
  };

  const contactCount = tag.contact_count ?? 0;
  const accountCount = tag.account_count ?? 0;
  const inUseCount = contactCount + accountCount;

  return (
    <li className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {editing ? (
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
                setError(null);
                setExisting(null);
              }}
              onKeyDown={onKey}
              maxLength={MAX_NAME}
              ref={inputRef}
              className="flex-1 rounded-md border border-[var(--border)] bg-transparent px-2 py-1 text-sm outline-none focus:border-[var(--accent)]"
            />
            <button
              type="button"
              onClick={submitRename}
              disabled={renameMutation.isPending}
              aria-label="Save"
              className="rounded-md border border-emerald-300 p-1 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50 dark:border-emerald-900 dark:text-emerald-300 dark:hover:bg-emerald-950/40"
            >
              {renameMutation.isPending ? (
                <Loader2 className="size-3.5 animate-spin" aria-hidden />
              ) : (
                <Check className="size-3.5" aria-hidden />
              )}
            </button>
            <button
              type="button"
              onClick={cancelEdit}
              aria-label="Cancel"
              disabled={renameMutation.isPending}
              className="rounded-md border border-[var(--border)] p-1 hover:bg-black/5 disabled:opacity-50 dark:hover:bg-white/5"
            >
              <X className="size-3.5" aria-hidden />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-full bg-black/5 px-2 py-0.5 text-sm font-medium dark:bg-white/10">
              <TagIcon className="size-3 opacity-60" aria-hidden />
              {tag.name}
            </span>
          </div>
        )}
        <div className="text-xs opacity-60">
          {inUseCount === 0
            ? "Unused"
            : `On ${contactCount} contact${contactCount === 1 ? "" : "s"} · ${accountCount} account${accountCount === 1 ? "" : "s"}`}
        </div>
        {error && <p className="text-xs text-red-700 dark:text-red-300">{error}</p>}
        {existing && (
          <p className="text-xs opacity-70">
            A tag named “{existing.name}” already exists — pick a different name.
          </p>
        )}
      </div>

      {!editing && (
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={startEdit}
            aria-label={`Rename ${tag.name}`}
            className="inline-flex items-center gap-1 rounded-md border border-[var(--border)] px-2 py-1 text-xs hover:bg-black/5 dark:hover:bg-white/5"
          >
            <Pencil className="size-3" aria-hidden />
            Rename
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={removeMutation.isPending}
            aria-label={`Delete ${tag.name}`}
            className="inline-flex items-center gap-1 rounded-md border border-[var(--border)] px-2 py-1 text-xs text-red-700 hover:bg-red-50 disabled:opacity-50 dark:text-red-300 dark:hover:bg-red-950/40"
          >
            {removeMutation.isPending ? (
              <Loader2 className="size-3 animate-spin" aria-hidden />
            ) : (
              <Trash2 className="size-3" aria-hidden />
            )}
            Delete
          </button>
        </div>
      )}
    </li>
  );
}
